'use client';

import React, { useEffect, useRef, useState } from 'react';

interface GhibliHeroVfxProps {
  imageSrc?: string;
  className?: string;
}

const VERTEX_SHADER_SOURCE = `
attribute vec2 a_position;
varying vec2 v_uv;

void main() {
  v_uv = (a_position + 1.0) * 0.5;
  // Flip Y for texture coordinates
  v_uv.y = 1.0 - v_uv.y;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER_SOURCE = `
precision highp float;

uniform sampler2D u_texture;
uniform vec2 u_resolution;
uniform vec2 u_image_size;
uniform float u_time;
uniform float u_motion_enabled;

varying vec2 v_uv;

// Fast procedural noise
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy) );
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 )) + i.x + vec3(0.0, i1.x, 1.0 ));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m ;
  m = m*m ;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  // Compute aspect ratio matching CSS background-size: cover
  vec2 canvasRatio = u_resolution;
  vec2 imgRatio = u_image_size;
  
  float sAspect = canvasRatio.x / canvasRatio.y;
  float iAspect = imgRatio.x / imgRatio.y;
  
  vec2 coverScale = vec2(1.0);
  if (sAspect > iAspect) {
    coverScale = vec2(1.0, iAspect / sAspect);
  } else {
    coverScale = vec2(sAspect / iAspect, 1.0);
  }
  
  vec2 uv = (v_uv - 0.5) * coverScale + 0.5;

  // If outside cover UV, clamp safely
  vec2 clampedUv = clamp(uv, 0.0, 1.0);

  if (u_motion_enabled < 0.5) {
    gl_FragColor = texture2D(u_texture, clampedUv);
    return;
  }

  // --- Shoreline & Solid Foreground Land Masking ---
  // Left bank (Tree, lantern, cottages, cat on ledge, stone wall):
  // Shore boundary curves from ~0.20 at horizon to ~0.15 at the bottom.
  // Pixels with clampedUv.x < leftShoreX are completely solid land.
  float leftShoreX = mix(0.20, 0.15, smoothstep(0.55, 0.95, clampedUv.y));
  float isLakeX = smoothstep(leftShoreX - 0.015, leftShoreX + 0.035, clampedUv.x);

  // Far shore / horizon (water surface begins below the distant village & mountains)
  float waterHorizon = 0.58;
  float isLakeY = smoothstep(waterHorizon - 0.02, waterHorizon + 0.04, clampedUv.y);

  // Right bank (Castle hill above water)
  float rightShoreX = 0.83;
  float notRightBank = 1.0 - smoothstep(rightShoreX - 0.03, rightShoreX + 0.03, clampedUv.x) * (1.0 - smoothstep(0.66, 0.74, clampedUv.y));

  // Combined true lake water mask (0.0 on cat, stone wall, tree, lantern, houses; 1.0 on lake water)
  float isWater = isLakeX * isLakeY * notRightBank;
  float depth = clamp((clampedUv.y - waterHorizon) / (1.0 - waterHorizon), 0.0, 1.0);

  // Gentle harmonic ripples specifically on the lake water
  float waveA = sin(clampedUv.y * (35.0 + depth * 40.0) - u_time * 1.3) * 0.004 * depth;
  float waveB = snoise(vec2(clampedUv.x * 14.0 + u_time * 0.35, clampedUv.y * 22.0 - u_time * 0.5)) * 0.0035 * depth;
  
  // Apply displacement ONLY to water (cat and land are 100% stable)
  vec2 displacedUv = clampedUv + vec2(waveA + waveB, (waveA - waveB) * 0.5) * isWater;
  displacedUv = clamp(displacedUv, 0.0, 1.0);

  vec4 color = texture2D(u_texture, displacedUv);

  // Subtle water surface caustic glimmer
  float causticIntensity = pow(clamp(abs(waveA + waveB) / (0.0055 * max(depth, 0.08)), 0.0, 1.0), 2.2);
  vec3 waterShimmer = vec3(0.35, 0.85, 0.95) * causticIntensity * 0.18 * isWater;

  // --- Cozy Ghibli Lantern Glow Pulse (left tree lantern, zero geometric distortion) ---
  vec2 lanternPos = vec2(0.072, 0.23);
  vec2 toLantern = (clampedUv - lanternPos) * vec2(u_image_size.x / u_image_size.y, 1.0);
  float lanternDist = length(toLantern);
  float lanternFlicker = 0.5 + 0.5 * (sin(u_time * 2.8) * 0.6 + sin(u_time * 5.3) * 0.4);
  float lanternHalo = smoothstep(0.22, 0.0, lanternDist) * lanternFlicker * 0.12;
  vec3 lanternWarmth = vec3(1.0, 0.68, 0.28) * lanternHalo;

  // --- Floating Ghibli Fireflies & Komorebi motes (lake & night sky) ---
  vec3 fireflyGlow = vec3(0.0);
  for (int i = 0; i < 9; i++) {
    float id = float(i);
    // Bias motes to open lake and sky area
    float px = 0.18 + 0.78 * fract(sin(id * 91.345 + 1.2) * 47453.54 + sin(u_time * 0.22 + id) * 0.07);
    float py = fract(cos(id * 37.123 + 4.8) * 31234.12 - u_time * 0.04 * (0.5 + fract(id * 0.3) * 0.6));
    
    vec2 pPos = vec2(px, py);
    vec2 diff = (v_uv - pPos) * vec2(u_resolution.x / u_resolution.y, 1.0);
    float dist = length(diff);
    
    float pulse = 0.5 + 0.5 * sin(u_time * 2.2 + id * 1.8);
    float glow = 0.0018 / (dist * dist + 0.00035) * pulse;
    
    vec3 moteColor = mix(vec3(0.98, 0.90, 0.45), vec3(0.40, 0.95, 0.80), fract(id * 0.45));
    fireflyGlow += moteColor * clamp(glow, 0.0, 1.0) * 0.32;
  }

  // --- Atmospheric sky breathing ---
  float skyBreathing = 1.0 + sin(u_time * 0.4) * 0.015 * (1.0 - isWater);
  vec3 finalRgb = (color.rgb * skyBreathing) + waterShimmer + fireflyGlow + lanternWarmth;

  gl_FragColor = vec4(finalRgb, color.a);
}
`;

export function GhibliHeroVfx({
  imageSrc = '/storefront/moonlit-lake.webp',
  className = '',
}: GhibliHeroVfxProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [webglSupported, setWebglSupported] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Check for prefers-reduced-motion
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let motionEnabled = !mediaQuery.matches;

    const onMotionChange = (e: MediaQueryListEvent) => {
      motionEnabled = !e.matches;
    };
    mediaQuery.addEventListener('change', onMotionChange);

    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) {
      setWebglSupported(false);
      return;
    }

    // Compile shader helper
    function createShader(type: number, source: string): WebGLShader | null {
      if (!gl) return null;
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    const vertShader = createShader(gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE);
    const fragShader = createShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER_SOURCE);
    if (!vertShader || !fragShader) {
      setWebglSupported(false);
      return;
    }

    const program = gl.createProgram();
    if (!program) {
      setWebglSupported(false);
      return;
    }
    gl.attachShader(program, vertShader);
    gl.attachShader(program, fragShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      setWebglSupported(false);
      return;
    }

    // Geometry: fullscreen quad
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1,
         1, -1,
        -1,  1,
        -1,  1,
         1, -1,
         1,  1,
      ]),
      gl.STATIC_DRAW
    );

    const aPosition = gl.getAttribLocation(program, 'a_position');
    const uResolution = gl.getUniformLocation(program, 'u_resolution');
    const uImageSize = gl.getUniformLocation(program, 'u_image_size');
    const uTime = gl.getUniformLocation(program, 'u_time');
    const uMotionEnabled = gl.getUniformLocation(program, 'u_motion_enabled');
    const uTexture = gl.getUniformLocation(program, 'u_texture');

    // Load background texture
    const texture = gl.createTexture();
    let isMounted = true;
    let imgWidth = 1920;
    let imgHeight = 1080;
    let textureLoaded = false;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageSrc;
    img.onload = () => {
      if (!isMounted || !gl || !canvas || !texture || gl.isContextLost()) return;
      imgWidth = img.naturalWidth || 1920;
      imgHeight = img.naturalHeight || 1080;

      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      textureLoaded = true;
    };

    let animationFrameId: number;
    let startTime = performance.now();
    let isVisible = true;

    // Pause rendering when hero is scrolled out of view to save GPU cycles
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
      },
      { threshold: 0.05 }
    );
    observer.observe(canvas);

    function resize() {
      if (!canvas || !gl) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = Math.floor(canvas.clientWidth * dpr);
      const height = Math.floor(canvas.clientHeight * dpr);

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    }

    function render() {
      if (isVisible && gl && textureLoaded) {
        resize();

        gl.useProgram(program);

        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.enableVertexAttribArray(aPosition);
        gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

        gl.uniform2f(uResolution, canvas!.width, canvas!.height);
        gl.uniform2f(uImageSize, imgWidth, imgHeight);

        const elapsedTime = (performance.now() - startTime) * 0.001;
        gl.uniform1f(uTime, elapsedTime);
        gl.uniform1f(uMotionEnabled, motionEnabled ? 1.0 : 0.0);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.uniform1i(uTexture, 0);

        gl.drawArrays(gl.TRIANGLES, 0, 6);
      }
      animationFrameId = requestAnimationFrame(render);
    }

    render();

    return () => {
      isMounted = false;
      cancelAnimationFrame(animationFrameId);
      observer.disconnect();
      mediaQuery.removeEventListener('change', onMotionChange);
      if (gl) {
        if (texture) gl.deleteTexture(texture);
        if (positionBuffer) gl.deleteBuffer(positionBuffer);
        if (program) gl.deleteProgram(program);
        if (vertShader) gl.deleteShader(vertShader);
        if (fragShader) gl.deleteShader(fragShader);
      }
    };
  }, [imageSrc]);

  if (!webglSupported) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      className={`ghibli-hero-canvas ${className}`}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}
